import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiPenNibLine, RiCheckboxCircleLine, RiCloseLine, RiHistoryLine } from 'react-icons/ri';
import authorizedSignerService, { AuthorizedSigner, AuthorizedSignerForm } from '../../services/authorizedSignerService';
import companyService from '../../services/companyService';
import { useCreateAuthorizedSigner, useUpdateAuthorizedSigner } from '../../hooks/mutations/useAuthorizedSignerMutations';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import { AUTHORIZED_SIGNERS_PATH, authorizedSignerDetailUrl } from '../../utils/authorizedSignerLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField } from '../../utils/mapFieldErrors';
import ConfirmModal from '../../components/ui/ConfirmModal';
import AuthorizedSignerFormFields from '../../components/authorized-signer/AuthorizedSignerFormFields';
import {
  emptyAuthorizedSignerForm,
  authorizedSignerToForm,
  authorizedSignerUpdatePayload,
  authorizedSignerIfMatch,
  validateAuthorizedSignerForm,
  AUTHORIZED_SIGNER_FIELD_LABELS,
  AUTHORIZED_SIGNER_VALUE_FORMATTERS,
} from '../../components/authorized-signer/authorizedSignerForm';
import {
  DetailPageLayout,
  DetailSection,
  RecordMetaCard,
  EntityActivityTimeline,
  useDetailNavigation,
} from '../../components/detail';

const LIST_PATH = AUTHORIZED_SIGNERS_PATH;

/**
 * หน้าเพิ่ม/แก้ไขผู้มีอำนาจลงนาม — `/master-data/authorized-signers/new` · `/:id/edit`
 * (แทนโมดัลเพิ่ม/แก้ไขใน AuthorizedSignerPage · มติ PO 28 ต.ค. 2026 · ต้นแบบ CompanyFormPage)
 *
 * ตรรกะบันทึกยกมาจาก handleSave ของโมดัลเดิมครบ: กันกดซ้ำ (ref · BUG-S803) · If-Match กันแก้ทับ (409 → โหลดค่าล่าสุด
 * ลงฟอร์มใหม่) · 422 field_errors → ใต้ช่อง · ที่เหลือ → alert ข้อความไทยจาก server
 * - หน้าเพิ่ม: `?company=<id>` เติมบริษัทให้ (หน้ารายการที่กรองบริษัทอยู่ส่งมา)
 * - หน้าแก้ไข: บริษัทอ่านอย่างเดียว + payload ไม่มี company_id (backend ไม่รับ — ดู authorizedSignerUpdatePayload)
 */
const AuthorizedSignerFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = id !== undefined;
  const signerId = isEdit ? parseRouteId(id) : null;
  const { hasPermission } = usePermission();
  const { backTo, goTo } = useDetailNavigation(LIST_PATH);
  const createMutation = useCreateAuthorizedSigner();
  const updateMutation = useUpdateAuthorizedSigner();

  const [formData, setFormData] = useState<AuthorizedSignerForm>(() => emptyAuthorizedSignerForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  /** ค่าตั้งต้นของฟอร์ม (JSON) — เทียบเพื่อรู้ว่าผู้ใช้แก้แล้วหรือยัง · null = ยังโหลดไม่เสร็จ */
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const signerQuery = useQuery({
    queryKey: queryKeys.authorizedSigners.detail(signerId ?? 0),
    queryFn: () => authorizedSignerService.getAuthorizedSigner(signerId!),
    enabled: isEdit && signerId !== null,
    // หน้าแก้ไขต้องได้ token If-Match ล่าสุดเสมอ — ไม่ใช้ cache เก่าจากหน้าดู
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const signer: AuthorizedSigner | undefined = signerQuery.data;

  // รายชื่อบริษัทใช้เฉพาะหน้าเพิ่ม (หน้าแก้ไขบริษัทล็อก — ชื่อมาจาก signer.company)
  const { data: companiesData, isLoading: companiesLoading } = useQuery({
    queryKey: ['companies', 'signer-form-options'],
    queryFn: () => companyService.getCompanies({ limit: 1000 }),
    enabled: !isEdit,
    staleTime: 5 * 60 * 1000,
  });
  const companies = companiesData?.items ?? [];

  // ตั้งค่าฟอร์มครั้งเดียวต่อรายการ (refetch ภายหลังไม่ทับสิ่งที่ผู้ใช้กำลังพิมพ์)
  const initializedFor = useRef<number | 'new' | null>(null);
  useEffect(() => {
    if (isEdit) {
      if (!signer || initializedFor.current === signer.id) return;
      initializedFor.current = signer.id;
      const f = authorizedSignerToForm(signer);
      setFormData(f);
      setInitialSnapshot(JSON.stringify(f));
      return;
    }
    if (initializedFor.current === 'new') return;
    initializedFor.current = 'new';
    const f = emptyAuthorizedSignerForm(parseRouteId(searchParams.get('company')) ?? 0);
    setFormData(f);
    setInitialSnapshot(JSON.stringify(f));
  }, [isEdit, signer, searchParams]);

  const isDirty = !saved && initialSnapshot !== null && JSON.stringify(formData) !== initialSnapshot;
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isDirty);

  const cancelTo = isEdit && signerId ? authorizedSignerDetailUrl(signerId) : backTo;

  const isSubmittingRef = useRef(false); // BUG-S803 double-submit guard
  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    // BUG-S803: synchronous guard กัน rapid double-click ยิง mutateAsync ซ้ำ same-tick
    if (isSubmittingRef.current) return;

    const errors = validateAuthorizedSignerForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setTimeout(() => {
        const first = Object.keys(errors)[0];
        const el = (document.querySelector(`[name="${first}"]`) ||
          document.querySelector(`[data-name="${first}"] input, [data-name="${first}"] button`)) as HTMLElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }, 50);
      return;
    }
    setFormErrors({});

    isSubmittingRef.current = true;
    try {
      let savedSigner: AuthorizedSigner;
      if (!isEdit) {
        savedSigner = await createMutation.mutateAsync(formData);
      } else {
        if (!signer) return;
        savedSigner = await updateMutation.mutateAsync({
          id: signer.id,
          data: authorizedSignerUpdatePayload(formData),
          // BUG-S162-P1: ส่ง token ของแถวตอนโหลด — มีคนแก้คั่นกลางจะได้ 409 แทนการทับเงียบ
          ifMatch: authorizedSignerIfMatch(signer),
        });
      }
      setSaved(true); // ปลดตัวกันออกจากหน้า ก่อนเปลี่ยนหน้า
      const savedId = savedSigner?.id ?? signer?.id;
      if (savedId && hasPermission('authorized_signers.view')) goTo(authorizedSignerDetailUrl(savedId), { replace: true });
      else goTo(backTo, { replace: true });
    } catch (error: any) {
      // BUG-S162-P1 + P-S601-1: 409 — hook แจ้งเตือนแล้ว · โหลดค่าล่าสุดจาก server ลงฟอร์มใหม่ (ได้ token ใหม่)
      // ห้ามคงค่าที่พิมพ์ไว้แล้วให้กดซ้ำด้วย token ใหม่ = เขียนทับงานของอีกคนโดยไม่เห็นว่าเขาแก้อะไร
      if (error?.response?.status === 409) {
        initializedFor.current = null;
        signerQuery.refetch();
        return;
      }
      // S1218 (BUG-S1218-001): 422 field_errors → ใต้ช่อง (hook แสดง alert ข้อความรวมแล้ว)
      const fieldErrs = mapFieldErrors(error);
      if (fieldErrs) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrs }));
        focusFirstInvalidField(fieldErrs);
        return;
      }
      console.error('Error saving signer:', error);
      // hook (useCreate/UpdateAuthorizedSigner) แสดงข้อความไทยจาก server แล้ว — ไม่แจ้งซ้อนอีกอัน
      if (!error?.response) showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถบันทึกข้อมูลผู้ลงนามได้', 'error');
    } finally {
      isSubmittingRef.current = false; // BUG-S803: reset ทุก exit path
    }
  };

  const title = isEdit ? 'แก้ไขข้อมูลผู้มีอำนาจลงนาม' : 'เพิ่มผู้มีอำนาจลงนาม';
  const breadcrumbs = useMemo(() => [
    { label: 'ผู้มีอำนาจลงนาม', path: backTo },
    ...(isEdit && signer ? [{ label: signer.full_name, path: authorizedSignerDetailUrl(signer.id) }] : []),
    { label: isEdit ? 'แก้ไข' : 'เพิ่มใหม่' },
  ], [backTo, isEdit, signer]);

  const notFound = isEdit && signerId === null ? { response: { status: 404 } } : null;

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumbs}
      icon={<RiPenNibLine />}
      title={title}
      recordId={isEdit ? signer?.id ?? signerId : undefined}
      subtitle={isEdit ? signer?.full_name : 'กรอกข้อมูลผู้มีอำนาจลงนามในสัญญาของบริษัทลูกค้า'}
      backTo={cancelTo}
      loading={(isEdit && signerQuery.isLoading) || (initialSnapshot === null && !notFound && !signerQuery.isError)}
      error={notFound || signerQuery.error}
      notFoundMessage="ไม่พบผู้มีอำนาจลงนามที่ระบุ"
      onRetry={() => signerQuery.refetch()}
      overlays={
        <ConfirmModal
          isOpen={showModal}
          onClose={onCancelLeave}
          onConfirm={onConfirmLeave}
          pushHistoryOnOpen={false}
          title="ข้อมูลยังไม่ได้บันทึก"
          message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
          confirmText="ออกจากหน้านี้"
          cancelText="อยู่ต่อ"
          isDanger={true}
        />
      }
      sidebar={
        isEdit && signer ? (
          <>
            <RecordMetaCard
              createdAt={signer.created_at}
              createdBy={signer.created_by}
              updatedAt={signer.updated_at}
              updatedBy={signer.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="authorized_signer"
                entityId={signer.id}
                fieldLabels={AUTHORIZED_SIGNER_FIELD_LABELS}
                valueFormatters={AUTHORIZED_SIGNER_VALUE_FORMATTERS}
              />
            </DetailSection>
          </>
        ) : undefined
      }
    >
      <DetailSection title="ข้อมูลพื้นฐาน" icon={<RiPenNibLine />}>
        {/* ไม่ใช้ <form onSubmit>: Enter ในช่องค้นหาของ SearchableSelect จะกลายเป็นกดบันทึก (โมดัลเดิมก็ไม่ใช้ native submit) */}
        <div>
          <AuthorizedSignerFormFields
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            companies={companies}
            companiesLoading={companiesLoading}
            companyLocked={isEdit}
            companyLabel={signer?.company ? [signer.company.code, signer.company.name].filter(Boolean).join(' - ') : undefined}
          />
          <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Link
              to={cancelTo}
              className="inline-flex items-center justify-center px-5 py-2.5 min-h-[44px] sm:min-h-0 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-all shadow-button"
            >
              <RiCloseLine className="w-4 h-4 mr-1.5" />
              ยกเลิก
            </Link>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center px-6 py-2.5 min-h-[44px] sm:min-h-0 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiCheckboxCircleLine className="w-4 h-4 mr-1.5" />
              {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
            </button>
          </div>
        </div>
      </DetailSection>
    </DetailPageLayout>
  );
};

export default AuthorizedSignerFormPage;
